import { ChevronsLeft, Menu } from 'lucide-react';
import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { Button } from 'v5/design-system/Button';
import { menuPaddingLeft } from 'v5/patterns/sidebar/menuPadding';
import { cn } from 'v5/platform/dom/cn';

export interface SidebarProps
  extends Omit<HTMLAttributes<HTMLElement>, 'onMouseEnter' | 'onMouseLeave'> {
  children: ReactNode;
}

export const Sidebar = forwardRef<HTMLElement, SidebarProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <aside
        ref={ref}
        {...props}
        className={cn(
          'transition-all text-fg-default',
          'flex flex-col h-full bg-bg-default [[data-sidebar-mode=collapsed]_&]:border-t [[data-sidebar-mode=collapsed]_&]:rounded-tr  border-r border-border-default z-40 [[data-sidebar-floating-hovered=true]_&]:shadow-xl',
          className
        )}
      >
        {children}
      </aside>
    );
  }
);
Sidebar.displayName = 'Sidebar';

// Sidebar Header
export interface SidebarHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const SidebarHeader = forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex-shrink-0 px-2 py-2 border-b border-border-muted',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarHeader.displayName = 'SidebarHeader';

// Sidebar Content
export interface SidebarContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const SidebarContent = forwardRef<HTMLDivElement, SidebarContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex-1 overflow-y-auto overflow-x-hidden py-2 isolate with-styled-scrollbars',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarContent.displayName = 'SidebarContent';

// Sidebar Footer
export interface SidebarFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const SidebarFooter = forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-2 shrink-0 px-2 py-2 border-t border-border-default',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarFooter.displayName = 'SidebarFooter';

// Sidebar Group
export interface SidebarGroupProps extends HTMLAttributes<HTMLDivElement> {
  label?: string;
  children: ReactNode;
}

export interface SidebarSectionHeaderProps
  extends HTMLAttributes<HTMLDivElement> {
  level?: number;
  children: ReactNode;
}

export const SidebarSectionHeader = forwardRef<
  HTMLDivElement,
  SidebarSectionHeaderProps
>(({ className, level = 0, children, style, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'py-1.5 pr-2 text-[13px] font-medium text-fg-muted',
        className
      )}
      style={{ paddingLeft: menuPaddingLeft(level), ...style }}
      {...props}
    >
      {children}
    </div>
  );
});
SidebarSectionHeader.displayName = 'SidebarSectionHeader';

export const SidebarGroup = forwardRef<HTMLDivElement, SidebarGroupProps>(
  ({ className, label, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('mb-2', className)} {...props}>
        {label && <SidebarSectionHeader>{label}</SidebarSectionHeader>}
        {children}
      </div>
    );
  }
);
SidebarGroup.displayName = 'SidebarGroup';

// Sidebar Toggle Button (shows hamburger icon, typically in header when collapsed)
export const SidebarToggle = forwardRef<
  HTMLButtonElement,
  HTMLAttributes<HTMLButtonElement>
>((props, ref) => {
  return (
    <Button
      ref={ref}
      variant="ghost"
      size="sm"
      layout="icon"
      aria-label="Show sidebar"
      {...props}
    >
      <Menu className="size-5" />
    </Button>
  );
});
SidebarToggle.displayName = 'SidebarToggle';

export const SidebarCollapse = forwardRef<
  HTMLButtonElement,
  HTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  return (
    <Button
      className={cn(
        '*:transition-transform *:duration-300 data-[dockable-sidebar-toggle-mode=collapsed]:*:-rotate-180 *:rotate-0',
        className
      )}
      variant="ghost"
      size="sm"
      layout="icon"
      ref={ref}
      aria-label="Collapse sidebar"
      {...props}
    >
      <ChevronsLeft className="size-4" />
    </Button>
  );
});
SidebarCollapse.displayName = 'SidebarCollapse';
